import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUsers,
  faChartLine,
  faChalkboardTeacher,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "../Overview/Overview.css";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Overview({ isMenuOpen }) {
  const getCurrentAcademicYear = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    return currentMonth >= 4
      ? `${currentYear}-${String(currentYear + 1).slice(-2)}`
      : `${currentYear - 1}-${String(currentYear).slice(-2)}`;
  };

  const currentYear = getCurrentAcademicYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [metrics, setMetrics] = useState({
    numberOfStudents: null,
    averageAttendance: null,
    numberOfTeachers: null,
  });
  const [loading, setLoading] = useState(true);
  const [classSectionData, setClassSectionData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [attendanceModalVisible, setAttendanceModalVisible] = useState(false);
  const [attendanceBreakdown, setAttendanceBreakdown] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const yearForRequest = selectedYear.includes("-")
          ? selectedYear.replace("-", "-20")
          : selectedYear;

        const response = await fetch(
          `https://aadyaacademy.org/api/dashboard/metrics?year=${yearForRequest}`
        );
        const data = await response.json();
        setMetrics({
          numberOfStudents: data.total_students,
          averageAttendance: data.average_attendance,
          numberOfTeachers: data.total_teachers,
        });
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics({ numberOfStudents: 0, averageAttendance: 0, numberOfTeachers: 0 });
      } finally {
        setLoading(false);
      }
    };

    if (selectedYear) {
      fetchMetrics();
    }
  }, [selectedYear]);

  const fetchClassSectionStrength = async () => {
    try {
      const response = await fetch(
        `https://aadyaacademy.org/api/dashboard/classSectionStrength`
      );
      const data = await response.json();
      const renamedData = data.map(item => ({
        ...item,
        class: item.class === "Mont Care" ? "Mont Care" : item.class,
      }));
      setClassSectionData(renamedData);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching class-section strength:", error);
      setClassSectionData([]);
      setShowModal(true);
    }
  };

  const fetchAttendanceBreakdown = async () => {
    try {
      const yearForRequest = selectedYear.includes("-")
        ? selectedYear.replace("-", "-20")
        : selectedYear;

      const response = await fetch(
        `https://aadyaacademy.org/api/dashboard/attendanceBreakdown?year=${yearForRequest}`
      );
      const data = await response.json();
      setAttendanceBreakdown(data);
      setAttendanceModalVisible(true);
    } catch (error) {
      console.error("Error fetching attendance breakdown:", error);
      setAttendanceBreakdown(null);
      setAttendanceModalVisible(true);
    }
  };

  const classOrder = [
    "Mont Care", "Prep I", "Prep II", "Prep III",
    "1", "2", "3", "4", "5", "6",
    "7", "8", "9", "10", "11", "12"
  ];

  const sortClasses = (classA, classB) => {
    const indexA = classOrder.indexOf(classA);
    const indexB = classOrder.indexOf(classB);
    return indexA - indexB;
  };

  return (
    <div className={`overview-container ${isMenuOpen ? "menu-open" : "menu-closed"}`}>
      <div className="overview-top">
        <h2>Overview</h2>
        <div className="year">
          <p><span>Academic year: </span>{selectedYear}</p>
        </div>
        <div className="report">
          <button className="generate-report-btn">
            <FontAwesomeIcon icon={faPlus} className="plus-icon" />
            Generate Report
          </button>
        </div>
      </div>

      <div className="overview">
        <div className="overview-card" onClick={fetchClassSectionStrength} style={{ cursor: "pointer" }}>
          <div className="card-content">
            <div className="card-content-tp">
              <div className="card-content-left"><FontAwesomeIcon icon={faUsers} /></div>
              <div className="card-content-mid">
                <h3>Number of Students</h3>
                <h2>{loading ? "Loading..." : metrics.numberOfStudents ?? "N/A"}</h2>
              </div>
            </div>
          </div>
        </div>

        <div className="overview-card" onClick={() => navigate("/teachers")} style={{ cursor: "pointer" }}>
          <div className="card-content">
            <div className="card-content-tp">
              <div className="card-content-left"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
              <div className="card-content-mid">
                <h3>Number of Teachers</h3>
                <h2>{loading ? "Loading..." : metrics.numberOfTeachers ?? "N/A"}</h2>
              </div>
            </div>
          </div>
        </div>

        <div className="overview-card" onClick={fetchAttendanceBreakdown} style={{ cursor: "pointer" }}>
          <div className="card-content">
            <div className="card-content-tp">
              <div className="card-content-left"><FontAwesomeIcon icon={faChartLine} /></div>
              <div className="card-content-mid">
                <h3>Average Attendance</h3>
                <h2>
                  {loading
                    ? "Loading..."
                    : metrics.averageAttendance !== null && !isNaN(metrics.averageAttendance)
                      ? `${metrics.averageAttendance}%`
                      : "N/A"}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Student Strength by Class & Section</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="class-section-list">
              {classSectionData && classSectionData.length > 0 ? (
                Object.entries(
                  classSectionData.reduce((acc, item) => {
                    const classKey = item.class;
                    if (!acc[classKey]) acc[classKey] = [];
                    acc[classKey].push(item);
                    return acc;
                  }, {})
                )
                  .sort(([a], [b]) => sortClasses(a, b))
                  .map(([className, sections]) => (
                    <div key={className} className="class-group">
                      <h4>{className}</h4>
                      <ul>
                        {sections.sort((a, b) => a.section.localeCompare(b.section)).map((sectionItem, idx) => (
                          <li key={idx}>
                            Section {sectionItem.section}: {sectionItem.strength} students
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
              ) : (
                <p>No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

 {/* Attendance breakdown modal */}
      {attendanceModalVisible && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <button className="modal-close-btn" onClick={() => setAttendanceModalVisible(false)}>×</button>
            </div>
            <div className="attendance-summary">
              {attendanceBreakdown?.summary ? (
                <>
     <ul style={{ display: "flex", gap: "20px", listStyle: "none", padding: 0 }}>
  <li
    style={{
      backgroundColor: "#28a745", // Green
      color: "#fff",
      padding: "15px 18px",
      borderRadius: "8px",
      flex: "1",
      textAlign: "center",
      fontWeight: "bold",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }}
  >
    <strong>Total Present:</strong> {attendanceBreakdown.summary.present}
  </li>
  <li
    style={{
      backgroundColor: "#dc3545", // Red
      color: "#fff",
      padding: "15px 15px",
      borderRadius: "8px",
      flex: "1",
      textAlign: "center",
      fontWeight: "bold",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }}
  >
    <strong>Total Absent:</strong> {attendanceBreakdown.summary.absent}
  </li>
  <li
    style={{
      backgroundColor: "#ffc107", // Yellow
      color: "#fff",
      padding: "15px 18px",
      borderRadius: "8px",
      flex: "1",
      textAlign: "center",
      fontWeight: "bold",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }}
  >
    <strong>Total Half Days:</strong> {attendanceBreakdown.summary.halfdays}
  </li>
</ul>


                  {attendanceBreakdown?.breakdown && (
                    <>
                      {Object.entries(
                        attendanceBreakdown.breakdown.reduce((acc, item) => {
                          const key = item.class;
                          if (!acc[key]) acc[key] = [];
                          acc[key].push(item);
                          return acc;
                        }, {})
                      )
                        .sort(([a], [b]) => sortClasses(a, b))
                        .map(([className, sections], index) => (
                          <div key={index} style={{ marginBottom: "40px" }}>
                            <h4 style={{ textAlign: "center" }}>{className}</h4>
                         <Bar
  data={{
    labels: sections.map(item => `Section ${item.section}`),
    datasets: [
      {
        label: "Present",
        data: sections.map(item => item.present),
        backgroundColor: "#28a745", // Solid Green
      },
      {
        label: "Absent",
        data: sections.map(item => item.absent),
        backgroundColor: "#dc3545", // Solid Red
      },
      {
        label: "Half Days",
        data: sections.map(item => item.halfdays),
        backgroundColor: "#ffc107", // Solid Yellow
      },
    ],
  }}
  options={{
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Attendance Breakdown - ${className}`,
      },
    },
    scales: {
      y: { beginAtZero: true },
    },
  }}
/>

                          </div>
                        ))}
                    </>
                  )}
                </>
              ) : (
                <p>No summary data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default Overview;
